import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Navigation, Globe } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { Spin, Alert } from 'antd';
import useLocationCheck from '../../hooks/useLocationCheck';
import GpsInstructions from '../../components/Location/GpsInstructions';
import './Location.css';

/**
 * Page de géolocalisation avec 2 options :
 * 1. Activer GPS (recommandé)
 * 2. Utiliser position IP temporaire
 */
const Location = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Hooks
  const { isChecking, retryGPS, isLocationValid } = useLocationCheck();
  const locationState = useSelector(state => state.location);
  const { gpsPermission } = locationState;

  // États locaux
  const [localLoading, setLocalLoading] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);

  /**
   * Redirection si localisation déjà valide
   */
  useEffect(() => {
    if (isLocationValid) {
      navigate('/home', { replace: true });
    }
  }, [isLocationValid, navigate]);

  /**
   * Afficher les instructions si permission GPS refusée
   */
  useEffect(() => {
    if (gpsPermission.status === 'denied') {
      setShowInstructions(true);
    }
  }, [gpsPermission.status]);

  /**
   * Handler pour activer le GPS
   */
  const handleEnableGPS = async () => {
    setLocalLoading(true);
    setRetryAttempt(prev => prev + 1);

    try {
      await retryGPS();

      // Si succès, la redirection se fera automatiquement via useEffect
    } catch (error) {
      console.error('[Location] GPS activation error:', error);
    } finally {
      setLocalLoading(false);
    }
  };

  /**
   * Handler pour utiliser position IP temporaire
   */
  const handleUseIPTemporary = async () => {
    setLocalLoading(true);

    try {
      // Lancer checkLocation qui fera GPS → IP
      await retryGPS();

      // Si on arrive ici avec succès (GPS ou IP), redirect
      // La redirection se fera automatiquement via useEffect
    } catch (error) {
      console.error('[Location] Error:', error);
    } finally {
      setLocalLoading(false);
    }
  };

  /**
   * Handler pour réessayer depuis les instructions
   */
  const handleRetryFromInstructions = () => {
    setShowInstructions(false);
    handleEnableGPS();
  };

  const isLoading = isChecking || localLoading;

  return (
    <div className="page-wrapper">
      <div className="page-content">
        <div className="container">
          <div className="location-page">
            {/* Header */}
            <div className="section-head text-center mb-4">
              <div
                className="icon-box bg-light rounded-circle mx-auto mb-3"
                style={{
                  width: '80px',
                  height: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Navigation size={40} className="text-primary" />
              </div>
              <h2>{t('location.title')}</h2>
              <p className="text-muted">{t('location.subtitle')}</p>
            </div>

            {/* Afficher les instructions si GPS refusé */}
            {showInstructions ? (
              <GpsInstructions onRetry={handleRetryFromInstructions} />
            ) : (
              <>
                {/* Message d'alerte si plusieurs tentatives */}
                {retryAttempt > 0 && gpsPermission.status === 'denied' && (
                  <Alert
                    message={t('location.errors.permissionDenied')}
                    description={t('location.errors.permissionDeniedDescription') ||
                      'Veuillez autoriser l\'accès à votre localisation dans les paramètres de votre navigateur'}
                    type="warning"
                    showIcon
                    className="mb-4"
                  />
                )}

                {/* Options de localisation */}
                <div className="location-options">
                  {/* Option 1 : GPS haute précision (recommandé) */}
                  <div className="location-option-card primary-option">
                    <div className="option-header">
                      <div className="option-icon">
                        <Navigation size={32} className="text-success" />
                      </div>
                      <div className="option-badge recommended">
                        {t('location.options.recommended') || 'Recommandé'}
                      </div>
                    </div>

                    <div className="option-content">
                      <h3 className="option-title">{t('location.options.useGPS') || 'Activer le GPS'}</h3>
                      <p className="option-description">
                        {t('location.options.useGPSDescription') || 'Précision ±50m - Recommandé pour les meilleurs résultats'}
                      </p>

                      {retryAttempt > 0 && (
                        <div className="retry-info">
                          {t('location.options.retrying', { count: retryAttempt }) || `Tentative ${retryAttempt}...`}
                        </div>
                      )}

                      <button
                        onClick={handleEnableGPS}
                        disabled={isLoading}
                        className="btn btn-primary btn-lg btn-location-action"
                        type="button"
                      >
                        {isLoading && <Spin size="small" className="mr-2" />}
                        {t('location.options.useGPS') || 'Activer le GPS'}
                      </button>
                    </div>
                  </div>

                  {/* Séparateur */}
                  <div className="divider">
                    <span>{t('location.instructions.alternative') || 'OU'}</span>
                  </div>

                  {/* Option 2 : Position IP temporaire */}
                  <div className="location-option-card secondary-option">
                    <div className="option-header">
                      <div className="option-icon">
                        <Globe size={32} className="text-info" />
                      </div>
                    </div>

                    <div className="option-content">
                      <h3 className="option-title">
                        {t('location.options.useIPTemporary') || 'Continuer avec position approximative'}
                      </h3>
                      <p className="option-description">
                        {t('location.options.useIPDescription') || 'Temporaire - Vous devrez activer le GPS plus tard'}
                      </p>

                      {/* Warning */}
                      <div className="option-warning">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        {t('location.options.ipWarning') ||
                          'Précision réduite (~10km). Vous devrez activer le GPS plus tard.'}
                      </div>

                      <button
                        onClick={handleUseIPTemporary}
                        disabled={isLoading}
                        className="btn btn-outline-secondary btn-lg btn-location-action"
                        type="button"
                      >
                        {isLoading && <Spin size="small" className="mr-2" />}
                        {t('location.options.useIPTemporary') || 'Continuer'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Info additionnelle */}
                <div className="location-info mt-4 text-center">
                  <p className="text-muted">
                    <i className="fas fa-info-circle mr-2"></i>
                    {t('location.info') ||
                      'Votre position est nécessaire pour trouver des profils près de chez vous'}
                  </p>
                </div>
              </>
            )}

            {/* Loader global */}
            {isLoading && !showInstructions && (
              <div className="text-center mt-4">
                <Spin size="large" tip={t('location.locating') || 'Localisation en cours...'} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Location;
